import React from 'react'
import { useEffect } from 'react'
import { useNavigate } from 'react-router'
import { Grid, Layout } from '@enterprise-ui/canvas-ui-react'
import { useAuth } from '@praxis/component-auth'
import { useAnalytics } from '@praxis/analytics/client'
import { SectionHeader } from '../../globalComponents/SectionHeader'
import { SavedViewContext } from '../../globalState/SavedViews/index'
import { HomePageCard } from './HomePageCard'
import { SavedViewsHomePageCard } from './SavedViewsHomePageCard'
import { trainingLinkUrl } from '../../globalConfig/constants'
import { CUSTOM_ANALYTICS_TYPES } from '../../globalConfig/constants'

export const HomePage = () => {
  const auth = useAuth()
  const userLanId = auth?.session?.userInfo?.lanId
  const { sendEvent } = useAnalytics()
  const navigate = useNavigate()
  const [state] = React.useContext(SavedViewContext)

  useEffect(() => {
    sendEvent({
      event: {
        type: CUSTOM_ANALYTICS_TYPES.PAGE_VIEW,
        id: 'Home Page',
      },
      client: {
        employee_login_id: userLanId,
      },
    })
  }, [sendEvent, userLanId])

  const cardDetails = {
    costSearch: {
      headerText: 'Cost Search',
      bodyText:
        'Use this page to search and update cost records through a user interface. Note that any submitted updates will not be effective until they are approved by Target.',
      buttonText: 'COST SEARCH',
      onButtonClick: () => {
        navigate('/cost-search')
      },
    },
    SpreadsheetDashboard: {
      headerText: 'Spreadsheet Dashboard',
      bodyText:
        'This page allows users to save time by updating costs in bulk through an Excel Spreadsheet. You can download a spreadsheet containing all of your current items and their costs, enter in your new costs, and then upload that spreadsheet into our system.',
      buttonText: 'SPREADSHEET DASHBOARD',
      onButtonClick: () => {
        navigate('/spreadsheet-dashboard')
      },
    },
    manageCustomLocationGroups: {
      headerText: 'Manage Custom Location Groups',
      bodyText:
        'Use this page to create and manage Custom Location Groups, which will allow you to group multiple locations of the same type together so that you can easily search and update across multiple locations with a single entry.',
      buttonText: 'MANAGE CUSTOM LOCATION GROUPS',
      onButtonClick: () => {
        navigate('/manage-custom-location-groups')
      },
    },
    manageNotificationsPage: {
      headerText: 'Manage Notifications',
      bodyText:
        'Use this page to update a subscribe to criteria and events you wish to be notified on.',
      buttonText: 'MANAGE NOTIFICATIONS',
      onButtonClick: () => {
        navigate('/manage-notifications')
      },
    },
    costChangeInsights: {
      headerText: 'Insights',
      bodyText:
        'Use this page to gain insights into all costs that have been submitted for your currently represented vendor.',
      buttonText: 'INSIGHTS',
      onButtonClick: () => {
        navigate('/insights')
      },
    },
  }

  const headerSubtext = (
    <span>
      {`Welcome to Target's cost management application. This application is
      intended to provide an easy and efficient way for vendors to send their
      cost data to Target.  Please visit our `}
      <a
        href={trainingLinkUrl}
        target="_blank"
        className="hesp-link"
        rel="noreferrer"
      >
        training materials
      </a>
      {` to learn what features are available and how to most effectively
      utilize this application.`}
    </span>
  )

  return (
    <>
      <Layout.Body className="hesp-generic-layout" data-testid="HomePage">
        <Grid.Container>
          <SectionHeader
            pageHeading="Cost Management"
            subtext={headerSubtext}
          />
          <Grid.Item className="hesp-general-fullwidth">
            <p className="hc-fs-md hc-mb-normal">
              <strong>Features</strong>
            </p>
            <div
              className="hesp-general-flex hesp-general-flex-wrap"
              style={{ gap: '40px' }}
            >
              {Object.values(cardDetails).map((card, index) => {
                return (
                  <HomePageCard
                    headerText={card.headerText}
                    bodyText={card.bodyText}
                    buttonText={card.buttonText}
                    onButtonClick={card.onButtonClick}
                    key={`homePageCard${index}`}
                  />
                )
              })}
            </div>
          </Grid.Item>
          <Grid.Item className="hesp-general-fullwidth hc-mt-normal">
            <p className="hc-fs-md hc-mb-normal">
              <strong>Your Saved Views</strong>
            </p>
            <SavedViewsHomePageCard savedViews={state.savedViews} />
          </Grid.Item>
        </Grid.Container>
      </Layout.Body>
    </>
  )
}
